import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import StaffPageShell from '../components/StaffPageShell';
import { ThemeButton } from '../components/ThemeWrapper';
import usePermissions from '../hooks/usePermissions';
import useLabFleet from '../hooks/useLabFleet';
import LabFleetMerge from '../components/LabFleetMerge';
import LabActionReport from '../components/LabActionReport';
import HomeBackupsButton from '../components/HomeBackupsButton';
import DeleteAccountDialog from '../components/DeleteAccountDialog';
import LabJobProgress from '../components/LabJobProgress';

const FILTERS = [
  ['all', 'All'],
  ['remove', 'Remove'],
  ['disable', 'Disable'],
  ['review', 'Review'],
  ['keep', 'Keep'],
];

const STATS = [
  ['accounts', 'Accounts'],
  ['keep', 'Keep'],
  ['disable', 'Disable'],
  ['remove', 'Remove'],
  ['review', 'Review'],
  ['vncActive', 'VNC active'],
  ['vncInactive', 'VNC inactive'],
  ['locked', 'Login locked'],
];

function expiryLabel(status, date) {
  const state = String(status || '').trim();
  const when = String(date || '').trim();
  if (state === 'expired') return when ? `Expired ${when}` : 'Expired';
  if (state === 'open') return when || 'Open';
  if (state === 'never' || when === 'never') return 'Never';
  return '—';
}

function accountExpiryLabel(row) {
  return expiryLabel(row.accountExpiryStatus, row.accountExpires);
}

function PolicyRemovalSpace({ summary, loading }) {
  const removal = summary || { accounts: 0, storageLabel: '', unmeasured: 0, servers: [] };
  const servers = Array.isArray(removal.servers) ? removal.servers : [];
  return (
    <section
      className="rounded-lg p-4 space-y-3"
      style={{ backgroundColor: '#fef2f2', border: '2px solid #b91c1c' }}
    >
      <div>
        <h2 className="text-base font-semibold" style={{ color: '#991b1b' }}>
          Space held by “Policy says delete this account”
        </h2>
        <p className="text-sm mt-1" style={{ color: '#7f1d1d' }}>
          These are the homes the lifecycle policy marks Remove. Delete leaves each home in place, so this disk stays on the server until that home is removed.
        </p>
      </div>
      <div className="text-3xl font-semibold" style={{ color: '#991b1b' }}>
        {loading && !removal.storageLabel ? '…' : removal.storageLabel || 'Size not fetched'}
        <span className="text-base font-normal ml-2">
          across {removal.accounts || 0} account{removal.accounts === 1 ? '' : 's'}
        </span>
      </div>
      {removal.unmeasured ? (
        <p className="text-xs" style={{ color: '#7f1d1d' }}>
          {removal.unmeasured} of those accounts have no home size yet. Fetch from servers to measure them.
        </p>
      ) : null}
      {servers.length ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {servers.map((server) => (
            <div key={server.alias} className="rounded-lg p-3 bg-white" style={{ border: '1px solid #fecaca' }}>
              <div className="text-xs uppercase tracking-wide" style={{ color: '#991b1b' }}>{server.alias}</div>
              <div className="text-2xl font-semibold theme-text">{server.storageLabel || 'Size not fetched'}</div>
              <div className="text-xs theme-text-secondary">
                {server.accounts} account{server.accounts === 1 ? '' : 's'}
                {server.unmeasured ? ` · ${server.unmeasured} not measured` : ''}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm" style={{ color: '#7f1d1d' }}>No account is marked Remove by the policy.</p>
      )}
    </section>
  );
}

function passwordExpiryLabel(row) {
  return expiryLabel(row.passwordExpiryStatus, row.passwordExpires);
}

function recommendationStyle(action) {
  if (action === 'remove') return { color: '#b91c1c', fontWeight: 600 };
  if (action === 'disable' || action === 'review') return { color: '#a16207', fontWeight: 600 };
  return undefined;
}

function LabFleetPage() {
  const { hasPermission, role } = usePermissions();
  const { fleet, loading, scanning, downloading, actionKey, error, notice, actionReport, dismissActionReport, reportNotice, load, scan, act, download, deleteTarget, cancelDelete, trackedJob, progressError, resumeProgress } = useLabFleet({
    extendExpiry: role === 'superadmin',
  });
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const accounts = useMemo(() => {
    const rows = fleet?.accounts || [];
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (filter !== 'all' && row.recommendation !== filter) return false;
      if (!q) return true;
      return [row.fullName, row.studentId, row.unixUsername, row.email, row.phone, row.alias, row.home, row.courseName]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [fleet, query, filter]);

  if (!hasPermission('import.pma')) {
    return (
      <StaffPageShell title="Server accounts" subtitle="Lab permission required">
        <p className="text-sm theme-text-secondary">This page needs the Lab Linux users permission.</p>
      </StaffPageShell>
    );
  }

  const summary = fleet?.summary || {};

  return (
    <StaffPageShell
      wide
      title="Server accounts"
      subtitle="Lab homes on this organization's servers, with login, VNC, storage, and whether to keep or remove the student."
    >
      <div className="profile-card theme-animate p-6 space-y-5">
        <p className="text-sm theme-text-secondary">
          Fetch reads each lab host in the active organization. It lists homes under /ve, whether the login is locked,
          the account expiry date from the server, whether that student's VNC service is active, and how much storage the home uses. The keep, disable, or remove
          mark comes from the same lifecycle policy as{' '}
          <Link className="underline" to="/admin/lab-cycle">
            Lab user cycle
          </Link>
          . Disable locks the login and stops VNC. Delete removes the local Linux account. The home stays, so its disk stays until that home is removed.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map(([key, label]) => (
            <div key={key} className="rounded-lg p-3" style={{ border: '1px solid var(--color-border)' }}>
              <div className="text-xs theme-text-secondary">{label}</div>
              <div className="text-2xl font-semibold theme-text">{summary[key] ?? (loading ? '…' : 0)}</div>
            </div>
          ))}
        </div>
        {summary.storageLabel ? (
          <p className="text-sm theme-text">Home storage on the last fetch: {summary.storageLabel}</p>
        ) : null}
        <PolicyRemovalSpace summary={summary.policyRemoval} loading={loading} />
        {fleet?.scannedAt ? (
          <p className="text-xs theme-text-secondary">Last server fetch: {fleet.scannedAt}</p>
        ) : (
          <p className="text-xs theme-text-secondary">
            Storage, login lock, and VNC are empty until you fetch the servers.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <ThemeButton type="button" onClick={scan} disabled={scanning || loading || downloading}>
            {scanning ? 'Fetching servers…' : 'Fetch from servers'}
          </ThemeButton>
          <HomeBackupsButton disabled={scanning || loading} onChanged={load} />
          <ThemeButton
            type="button"
            onClick={() => download({ recommendation: filter, q: query })}
            disabled={loading || scanning || downloading || !accounts.length}
          >
            {downloading ? 'Preparing Excel…' : 'Download Excel'}
          </ThemeButton>
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className="text-sm underline"
              onClick={() => setFilter(key)}
              style={filter === key ? { fontWeight: 600 } : undefined}
            >
              {label}
            </button>
          ))}
          <input
            className="px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            placeholder="Search name, id, or server"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        {notice ? <p className="text-sm" style={{ color: '#166534' }}>{notice}</p> : null}
        <LabJobProgress job={trackedJob} error={progressError} onRetry={resumeProgress} />
        <LabActionReport report={actionReport} onClose={dismissActionReport} />
        <DeleteAccountDialog
          account={deleteTarget}
          busy={Boolean(actionKey)}
          onCancel={cancelDelete}
          onConfirm={(keepBackup) => act(deleteTarget, 'delete', { keepBackup })}
        />
        {error ? <p className="text-sm" style={{ color: '#b91c1c' }}>{error}</p> : null}
        {(fleet?.hostResults || []).some((host) => host.ssh === 'FAILED') ? (
          <ul className="text-sm space-y-1">
            {fleet.hostResults
              .filter((host) => host.ssh === 'FAILED')
              .map((host) => (
                <li key={host.alias} style={{ color: '#b91c1c' }}>
                  {host.alias}: {host.error}
                </li>
              ))}
          </ul>
        ) : null}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left theme-text-secondary">
                <th className="pr-3 py-2">Student</th>
                <th className="pr-3 py-2">Server</th>
                <th className="pr-3 py-2">Folder</th>
                <th className="pr-3 py-2">Email</th>
                <th className="pr-3 py-2">Phone</th>
                <th className="pr-3 py-2">Login</th>
                <th className="pr-3 py-2">Account expires</th>
                <th className="pr-3 py-2">Password expires</th>
                <th className="pr-3 py-2">Last login</th>
                <th className="pr-3 py-2">VNC</th>
                <th className="pr-3 py-2">Storage</th>
                <th className="pr-3 py-2">Decision</th>
                <th className="pr-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((row) => {
                const busy = actionKey.startsWith(`${row.unixUsername}:`);
                return (
                  <tr key={`${row.unixUsername}-${row.alias}-${row.home}-${row.studentId}`} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td className="pr-3 py-2 align-top">
                      <div className="font-medium theme-text">{row.fullName || row.unixUsername}</div>
                      <div className="font-mono text-xs">{row.unixUsername}</div>
                      <div className="text-xs theme-text-secondary">
                        {row.studentId} {row.courseName} {row.batchName}
                      </div>
                    </td>
                    <td className="pr-3 py-2 align-top">
                      {row.alias || '—'}
                      <div className="text-xs theme-text-secondary">{row.serverStatusLabel || row.cfgStatus || row.labStatus || ''}</div>
                      {row.homeBackup ? <div className="font-mono text-xs theme-text-secondary">Backup {row.homeBackup}</div> : null}
                      {row.resourceLabel ? <div className="text-xs theme-text-secondary">{row.resourceLabel}</div> : null}
                    </td>
                    <td className="pr-3 py-2 align-top font-mono text-xs">{row.home || '—'}</td>
                    <td className="pr-3 py-2 align-top">{row.email || '—'}</td>
                    <td className="pr-3 py-2 align-top">{row.phone || '—'}</td>
                    <td className="pr-3 py-2 align-top">{row.lock === 'unknown' ? '—' : row.lock}</td>
                    <td
                      className="pr-3 py-2 align-top whitespace-nowrap"
                      style={row.accountExpiryStatus === 'expired' ? { color: '#b91c1c', fontWeight: 600 } : undefined}
                    >
                      {accountExpiryLabel(row)}
                    </td>
                    <td
                      className="pr-3 py-2 align-top whitespace-nowrap"
                      style={row.passwordExpiryStatus === 'expired' ? { color: '#b91c1c', fontWeight: 600 } : undefined}
                    >
                      {passwordExpiryLabel(row)}
                    </td>
                    <td className="pr-3 py-2 align-top whitespace-nowrap">
                      {row.lastLogin === 'never' ? 'Never' : row.lastLogin || '—'}
                    </td>
                    <td className="pr-3 py-2 align-top">{row.vnc === 'unknown' ? '—' : row.vnc}</td>
                    <td className="pr-3 py-2 align-top">
                      {row.storageLabel || '—'}
                      {row.quotaHardKb ? (
                        <div className="text-xs theme-text-secondary">
                          quota {Math.round(row.quotaUsedKb / 1024) || 0} MB of {Math.round(row.quotaHardKb / 1024)} MB
                        </div>
                      ) : null}
                    </td>
                    <td className="pr-3 py-2 align-top" style={recommendationStyle(row.recommendation)}>
                      <div>{row.recommendation}</div>
                      <div className="text-xs font-normal theme-text-secondary">{row.recommendationLabel}</div>
                    </td>
                    <td className="pr-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        <ThemeButton
                          type="button"
                          onClick={() => act(row, 'enable')}
                          disabled={!row.canEnable || busy || scanning}
                        >
                          Enable
                        </ThemeButton>
                        <ThemeButton
                          type="button"
                          onClick={() => act(row, 'disable')}
                          disabled={!row.canAct || busy || scanning}
                        >
                          Disable
                        </ThemeButton>
                        <ThemeButton
                          type="button"
                          onClick={() => act(row, 'delete')}
                          disabled={!row.canAct || busy || scanning}
                        >
                          Delete
                        </ThemeButton>
                      </div>
                      {!row.inSms ? (
                        <LabFleetMerge
                          account={row}
                          disabled={busy || scanning}
                          onMerged={async (message) => {
                            reportNotice(message);
                            await load();
                          }}
                        />
                      ) : null}
                      {!row.canEnable ? (
                        <div className="text-xs theme-text-secondary mt-1">This server is not in the lab host list.</div>
                      ) : !row.canAct ? (
                        <div className="text-xs theme-text-secondary mt-1">Disable and delete need an SMS lab row.</div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && !accounts.length ? (
            <p className="text-sm theme-text-secondary py-3">No matching accounts.</p>
          ) : null}
        </div>
      </div>
    </StaffPageShell>
  );
}

export default LabFleetPage;
